import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ContentManager from './ContentManager';
import { BookOpenIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useApi } from '../../hooks/useApi';
import PremiumTooltip from '../common/PremiumTooltip';
import LessonPlanModal from './LessonPlanModal';

const LessonCard = ({ lesson, onEdit, onDelete, onRefresh, courseId }) => {
    const navigate = useNavigate();
    const { post } = useApi();
    const [showContentManager, setShowContentManager] = useState(false);
    const [showLessonPlanModal, setShowLessonPlanModal] = useState(false);

    const handleManageContent = () => {
        setShowContentManager(true);
    };

    const handleLessonPlanClick = () => {
        setShowLessonPlanModal(true);
    };

    return (
        <div className="bg-white rounded-[2rem] border border-gray-100 hover:shadow-2xl hover:shadow-indigo-500/10 hover:border-indigo-100 transition-all p-6 group cursor-default">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                <div className="flex-1 w-full">
                    <div className="flex items-center space-x-3 mb-3">
                        <h4 className="text-base md:text-lg font-extrabold text-[#18216D] transition-colors uppercase tracking-tight leading-tight">{lesson.name || lesson.title}</h4>
                    </div>
                    {lesson.summary && (
                        <p className="text-sm text-gray-500 mb-4 font-medium line-clamp-2 leading-relaxed">{lesson.summary}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 md:gap-3 text-[10px] font-black uppercase tracking-widest text-gray-400">
                        <span className="px-2 py-1 bg-slate-50 rounded-lg border border-slate-100 italic">Sub-strand {lesson.order}</span>
                        {lesson.duration_minutes > 0 && (
                            <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">{lesson.duration_minutes} min</span>
                        )}
                        <span className="px-2 py-1 bg-purple-50 text-purple-600 rounded-lg border border-purple-100">{lesson.contents?.length || 0} Blocks</span>
                    </div>
                </div>

                {/* Actions Section */}
                <div className="flex items-center justify-between md:justify-end gap-3 w-full md:w-auto border-t md:border-t-0 pt-4 md:pt-0 border-slate-50">
                    <div className="flex items-center gap-2">
                        {/* Lesson Plan button */}
                        <PremiumTooltip text="Generate Lesson Plan">
                            <button
                                onClick={handleLessonPlanClick}
                                className="p-3 rounded-2xl transition-all border shadow-sm flex items-center gap-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white border-emerald-100 hover:shadow-emerald-100 active:scale-95"
                            >
                                <BookOpenIcon className="w-5 h-5" />
                                <span className="text-[10px] font-black uppercase tracking-widest hidden lg:inline">Lesson Plan</span>
                            </button>
                        </PremiumTooltip>

                        {/* Manage Content button */}
                        <PremiumTooltip text="Upload Materials / Notes">
                            <button
                                onClick={handleManageContent}
                                className="p-3 text-purple-600 bg-purple-50 hover:bg-purple-600 hover:text-white border border-purple-100 rounded-2xl transition-all shadow-sm active:scale-95"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </button>
                        </PremiumTooltip>
                    </div>

                    <div className="flex items-center gap-1 bg-slate-50/50 p-1 rounded-xl border border-slate-100/50">
                        <PremiumTooltip text="Edit Sub-Strand">
                            <button
                                onClick={() => onEdit(lesson)}
                                className="p-2.5 text-[#18216D] hover:bg-white hover:shadow-sm rounded-lg transition-all active:scale-95"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                            </button>
                        </PremiumTooltip>
                        <PremiumTooltip text="Delete Sub-Strand">
                            <button
                                onClick={() => onDelete(lesson)}
                                className="p-2.5 text-rose-500 hover:bg-rose-50 rounded-lg transition-all active:scale-95"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        </PremiumTooltip>
                    </div>
                </div>
            </div>

            {/* Modals */}
            {showContentManager && (
                <ContentManager
                    lesson={lesson}
                    onClose={() => setShowContentManager(false)}
                    onSave={onRefresh}
                />
            )}
            
            {showLessonPlanModal && (
                <LessonPlanModal
                    subStrand={lesson}
                    courseId={courseId}
                    onClose={() => setShowLessonPlanModal(false)}
                />
            )}
        </div>
    );
};

export default LessonCard;
